import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  api,
  type LeagueImportPreview,
  type LeagueTeamPreview,
  type LeagueImportResult,
  type ParsedPlayer,
} from '../../services/api';

import './CoachInviteList';

type WizardStep = 'upload' | 'preview' | 'complete';

@customElement('league-import-wizard')
export class LeagueImportWizard extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private step: WizardStep = 'upload';

  @state()
  private file: File | null = null;

  @state()
  private preview: LeagueImportPreview | null = null;

  @state()
  private result: LeagueImportResult | null = null;

  @state()
  private loading = false;

  @state()
  private error = '';

  @state()
  private skipDuplicates = false;

  @state()
  private expandedTeams: Set<number> = new Set();

  private handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0] || null;
    if (f && !f.name.toLowerCase().endsWith('.xlsx')) {
      this.error = 'Please upload an .xlsx file';
      this.file = null;
      return;
    }
    this.file = f;
    this.error = '';
  }

  private handleDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.toLowerCase().endsWith('.xlsx')) {
        this.file = file;
        this.error = '';
      } else {
        this.error = 'Please upload an .xlsx file';
      }
    }
  }

  private async uploadFile() {
    if (!this.file) return;
    this.loading = true;
    this.error = '';
    try {
      this.preview = await api.uploadLeagueXLSX(this.file);
      // Expand all teams by default
      this.expandedTeams = new Set(this.preview.teams.map((_, i) => i));
      this.step = 'preview';
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Upload failed';
    } finally {
      this.loading = false;
    }
  }

  private async confirmImport() {
    if (!this.preview) return;
    this.loading = true;
    this.error = '';
    try {
      this.result = await api.confirmLeagueImport(
        this.preview.import_id,
        this.skipDuplicates
      );
      this.step = 'complete';
      this.dispatchEvent(new CustomEvent('import-complete'));
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Import failed';
    } finally {
      this.loading = false;
    }
  }

  private async cancelImport() {
    if (!this.preview) return;
    try {
      await api.cancelLeagueImport(this.preview.import_id);
    } catch (err) {
      console.error('Failed to cancel import:', err);
    }
    this.reset();
  }

  private toggleTeam(index: number) {
    const next = new Set(this.expandedTeams);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    this.expandedTeams = next;
  }

  private updatePlayer(teamIndex: number, playerIndex: number, field: keyof ParsedPlayer, value: string) {
    if (!this.preview) return;
    
    // Create a deep copy to trigger reactivity
    const updatedTeams = this.preview.teams.map((team, ti) => {
      if (ti !== teamIndex) return team;
      return {
        ...team,
        players: team.players.map((player, pi) => {
          if (pi !== playerIndex) return player;
          const updated = { ...player, [field]: value };
          // Clear errors when user edits a field
          if (updated.errors && updated.errors.length > 0) {
            updated.errors = updated.errors.filter(e => !e.toLowerCase().includes(field.toLowerCase()));
          }
          return updated;
        }),
      };
    });
    
    this.preview = { ...this.preview, teams: updatedTeams };
  }

  // Convert various DOB formats to YYYY-MM-DD for date input
  private dobToInputValue(dob: string | undefined): string {
    if (!dob) return '';
    
    // Try parsing common formats
    const formats = [
      // ISO format
      { regex: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, order: [1, 2, 3] },
      // US format M/D/YYYY or MM/DD/YYYY
      { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: [3, 1, 2] },
      // European format D/M/YYYY or DD/MM/YYYY  
      { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: [3, 2, 1], checkEuro: true },
    ];
    
    for (const fmt of formats) {
      const match = dob.match(fmt.regex);
      if (match) {
        let year = match[fmt.order[0]];
        let month = match[fmt.order[1]].padStart(2, '0');
        let day = match[fmt.order[2]].padStart(2, '0');
        
        // For ambiguous slash formats, detect US vs European
        if (fmt.checkEuro) {
          const first = parseInt(match[1]);
          const second = parseInt(match[2]);
          // If second > 12, it must be day (US format)
          if (second > 12) {
            month = match[1].padStart(2, '0');
            day = match[2].padStart(2, '0');
          }
          // If first > 12, it must be day (European format)
          else if (first > 12) {
            day = match[1].padStart(2, '0');
            month = match[2].padStart(2, '0');
          }
          // Ambiguous - assume US format
          else {
            month = match[1].padStart(2, '0');
            day = match[2].padStart(2, '0');
          }
        }
        
        return `${year}-${month}-${day}`;
      }
    }
    
    return '';
  }

  // Convert date input YYYY-MM-DD to display format MM/DD/YYYY
  private inputValueToDob(value: string): string {
    if (!value) return '';
    const [year, month, day] = value.split('-');
    return `${month}/${day}/${year}`;
  }

  private reset() {
    this.step = 'upload';
    this.file = null;
    this.preview = null;
    this.result = null;
    this.error = '';
    this.skipDuplicates = false;
    this.expandedTeams = new Set();
  }

  render() {
    return html`
      <div class="card bg-base-100 shadow-lg">
        <div class="card-body p-4 sm:p-8">
          <h2 class="card-title text-base sm:text-xl">League Import</h2>

          <!-- Steps indicator -->
          <ul class="steps steps-horizontal mb-4 sm:mb-6 w-full text-xs sm:text-sm">
            <li class="step ${this.step !== 'upload' ? 'step-primary' : ''}">
              Upload
            </li>
            <li class="step ${['preview', 'complete'].includes(this.step) ? 'step-primary' : ''}">
              Preview
            </li>
            <li class="step ${this.step === 'complete' ? 'step-primary' : ''}">
              Complete
            </li>
          </ul>

          ${this.error
            ? html`
                <div class="alert alert-error mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>${this.error}</span>
                </div>
              `
            : nothing}

          ${this.step === 'upload' ? this.renderUpload() : nothing}
          ${this.step === 'preview' ? this.renderPreview() : nothing}
          ${this.step === 'complete' ? this.renderComplete() : nothing}
        </div>
      </div>
    `;
  }

  private renderUpload() {
    return html`
      <div
        class="border-2 border-dashed border-base-300 rounded-lg p-4 sm:p-8 text-center"
        @dragover=${this.handleDragOver}
        @drop=${this.handleDrop}
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-12 w-12 text-base-content/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p class="mt-4 text-base-content/70 text-sm sm:text-base">
          Drag and drop an XLSX file, or click to select
        </p>
        <input
          type="file"
          accept=".xlsx"
          class="file-input file-input-bordered file-input-primary file-input-sm sm:file-input-md mt-4 max-w-full"
          @change=${this.handleFileSelect}
        />
        ${this.file
          ? html`<p class="mt-4 font-medium text-sm sm:text-base truncate">${this.file.name}</p>`
          : nothing}
      </div>

      <div class="alert alert-info mt-4 text-sm sm:text-base">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 h-5 w-5 sm:h-6 sm:w-6">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>
          Upload a RAMP/MHSAA league-wide XLSX export. Players will be
          grouped by team automatically.
        </span>
      </div>

      <div class="card-actions justify-end mt-4">
        <button
          class="btn btn-primary btn-sm sm:btn-md ${this.loading ? 'loading' : ''}"
          ?disabled=${!this.file || this.loading}
          @click=${this.uploadFile}
        >
          ${this.loading ? 'Uploading...' : 'Upload & Preview'}
        </button>
      </div>
    `;
  }

  private renderPreview() {
    if (!this.preview) return nothing;

    const { summary, teams } = this.preview;
    const hasDuplicates = teams.some(t =>
      t.players.some(p => p.is_duplicate)
    );

    return html`
      <!-- Season banner -->
      <div class="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 p-3 bg-base-200 rounded-lg">
        <span class="text-base sm:text-lg font-semibold">Season:</span>
        <span class="font-medium text-sm sm:text-base">${this.preview.season_name || 'Unknown'}</span>
        <span class="badge ${this.preview.is_new_season ? 'badge-accent' : 'badge-ghost'} badge-sm sm:badge-md">
          ${this.preview.is_new_season ? 'New' : 'Existing'}
        </span>
      </div>

      <!-- Summary stats -->
      <div class="stats shadow mb-4 w-full stats-vertical sm:stats-horizontal">
        <div class="stat">
          <div class="stat-title">Teams</div>
          <div class="stat-value text-lg">${summary.total_teams}</div>
          <div class="stat-desc">${summary.new_teams} new</div>
        </div>
        <div class="stat">
          <div class="stat-title">Players</div>
          <div class="stat-value text-lg">${summary.total_players}</div>
        </div>
        <div class="stat">
          <div class="stat-title">New</div>
          <div class="stat-value text-lg text-success">${summary.new_players}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Updates</div>
          <div class="stat-value text-lg text-warning">${summary.updated_players}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Errors</div>
          <div class="stat-value text-lg text-error">${summary.errors}</div>
        </div>
      </div>

      <!-- Per-team sections -->
      <div class="space-y-2 mb-4">
        ${teams.map((team, index) => this.renderTeamSection(team, index))}
      </div>

      ${hasDuplicates
        ? html`
            <div class="form-control mb-4">
              <label class="label cursor-pointer">
                <span class="label-text">
                  Skip duplicate players (don't update existing records)
                </span>
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary"
                  .checked=${this.skipDuplicates}
                  @change=${(e: Event) => {
                    this.skipDuplicates = (e.target as HTMLInputElement).checked;
                  }}
                />
              </label>
            </div>
          `
        : nothing}

      <div class="card-actions flex-col-reverse sm:flex-row justify-between mt-4 gap-2">
        <button class="btn btn-ghost btn-sm sm:btn-md w-full sm:w-auto" @click=${this.cancelImport}>
          Cancel
        </button>
        <button
          class="btn btn-primary btn-sm sm:btn-md w-full sm:w-auto ${this.loading ? 'loading' : ''}"
          ?disabled=${this.loading}
          @click=${this.confirmImport}
        >
          ${this.loading ? 'Importing...' : `Confirm (${summary.total_teams} teams)`}
        </button>
      </div>
    `;
  }

  private renderTeamSection(team: LeagueTeamPreview, index: number) {
    const isExpanded = this.expandedTeams.has(index);
    const playerCount = team.players.filter(p => !p.is_staff).length;
    const staffCount = team.players.filter(p => p.is_staff).length;

    return html`
      <div class="collapse collapse-arrow bg-base-200 rounded-lg">
        <input
          type="checkbox"
          .checked=${isExpanded}
          @change=${() => this.toggleTeam(index)}
        />
        <div class="collapse-title pr-12">
          <div class="flex flex-wrap items-center gap-1 sm:gap-2">
            <span class="font-semibold text-sm sm:text-base">${team.team_name}</span>
            <span class="badge ${team.is_new_team ? 'badge-accent' : 'badge-ghost'} badge-sm">
              ${team.is_new_team ? 'New Team' : 'Existing'}
            </span>
            <span class="text-xs sm:text-sm text-base-content/60 ml-auto">
              ${playerCount} player${playerCount !== 1 ? 's' : ''}${staffCount > 0 ? `, ${staffCount} staff` : ''}
            </span>
          </div>
        </div>
        <div class="collapse-content">
          <!-- Mobile card layout -->
          <div class="sm:hidden space-y-2 max-h-64 overflow-y-auto">
            ${team.players.map((player, playerIndex) => this.renderPlayerCard(player, index, playerIndex))}
          </div>
          <!-- Desktop table layout -->
          <div class="hidden sm:block overflow-x-auto max-h-64">
            <table class="table table-zebra table-sm">
              <thead class="sticky top-0 bg-base-200">
                <tr>
                  <th>Name</th>
                  <th>DOB</th>
                  <th>Jersey</th>
                  <th>Status</th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                ${team.players.map((player, playerIndex) => this.renderPlayerRow(player, index, playerIndex))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  private renderPlayerCard(player: ParsedPlayer, teamIndex: number, playerIndex: number) {
    const hasErrors = player.errors && player.errors.length > 0;
    const bgClass = hasErrors
      ? 'bg-error/10 border-error/30'
      : player.is_duplicate
        ? 'bg-warning/10 border-warning/30'
        : 'bg-base-100 border-base-300';

    return html`
      <div class="border rounded-lg p-2.5 ${bgClass}">
        <div class="flex items-center justify-between gap-2">
          <input
            type="text"
            class="input input-xs input-bordered flex-1 min-w-0 font-medium"
            .value=${player.name}
            @input=${(e: Event) => this.updatePlayer(teamIndex, playerIndex, 'name', (e.target as HTMLInputElement).value)}
            placeholder="Name"
          />
          <div class="flex gap-1 shrink-0">
            ${hasErrors
              ? html`<span class="badge badge-error badge-sm">Error</span>`
              : player.is_duplicate
                ? html`<span class="badge badge-warning badge-sm">Update</span>`
                : html`<span class="badge badge-success badge-sm">New</span>`}
            ${player.is_staff
              ? html`<span class="badge badge-info badge-sm">Staff</span>`
              : nothing}
          </div>
        </div>
        <div class="flex gap-2 mt-2">
          <input
            type="date"
            class="input input-xs input-bordered w-32"
            .value=${this.dobToInputValue(player.dob)}
            max=${new Date().toISOString().split('T')[0]}
            min="1900-01-01"
            @change=${(e: Event) => this.updatePlayer(teamIndex, playerIndex, 'dob', this.inputValueToDob((e.target as HTMLInputElement).value))}
          />
          <input
            type="text"
            class="input input-xs input-bordered w-16"
            .value=${player.jersey ?? ''}
            @input=${(e: Event) => this.updatePlayer(teamIndex, playerIndex, 'jersey', (e.target as HTMLInputElement).value)}
            placeholder="#"
          />
        </div>
        ${hasErrors
          ? html`<p class="text-error text-xs mt-1">${player.errors?.join(', ')}</p>`
          : nothing}
      </div>
    `;
  }

  private renderPlayerRow(player: ParsedPlayer, teamIndex: number, playerIndex: number) {
    const hasErrors = player.errors && player.errors.length > 0;
    const rowClass = hasErrors
      ? 'bg-error/20'
      : player.is_duplicate
        ? 'bg-warning/20'
        : '';

    return html`
      <tr class=${rowClass}>
        <td>
          <input
            type="text"
            class="input input-xs input-bordered w-full min-w-[120px]"
            .value=${player.name}
            @input=${(e: Event) => this.updatePlayer(teamIndex, playerIndex, 'name', (e.target as HTMLInputElement).value)}
          />
        </td>
        <td>
          <input
            type="date"
            class="input input-xs input-bordered w-32"
            .value=${this.dobToInputValue(player.dob)}
            max=${new Date().toISOString().split('T')[0]}
            min="1900-01-01"
            @change=${(e: Event) => this.updatePlayer(teamIndex, playerIndex, 'dob', this.inputValueToDob((e.target as HTMLInputElement).value))}
          />
        </td>
        <td>
          <input
            type="text"
            class="input input-xs input-bordered w-16"
            .value=${player.jersey ?? ''}
            @input=${(e: Event) => this.updatePlayer(teamIndex, playerIndex, 'jersey', (e.target as HTMLInputElement).value)}
          />
        </td>
        <td>
          ${hasErrors
            ? html`<span class="badge badge-error badge-sm">Error</span>`
            : player.is_duplicate
              ? html`<span class="badge badge-warning badge-sm">Update</span>`
              : html`<span class="badge badge-success badge-sm">New</span>`}
          ${player.is_staff
            ? html`<span class="badge badge-info badge-sm ml-1">Staff</span>`
            : nothing}
        </td>
        <td>
          ${hasErrors
            ? html`<span class="text-error text-sm">${player.errors?.join(', ')}</span>`
            : nothing}
        </td>
      </tr>
    `;
  }

  private renderComplete() {
    if (!this.result) return nothing;

    const coaches = this.result.coaches || [];

    return html`
      <div class="text-center py-8">
        <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-16 w-16 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 class="text-xl font-semibold mt-4">League Import Complete!</h3>
        <div class="mt-4 space-y-1 text-base-content/70">
          ${this.result.teams_created > 0
            ? html`<p>${this.result.teams_created} new team${this.result.teams_created !== 1 ? 's' : ''} created</p>`
            : nothing}
          <p>${this.result.players_imported} new player${this.result.players_imported !== 1 ? 's' : ''} imported</p>
          ${this.result.players_updated > 0
            ? html`<p>${this.result.players_updated} existing player${this.result.players_updated !== 1 ? 's' : ''} updated</p>`
            : nothing}
        </div>

        ${coaches.length > 0
          ? html`
              <div class="divider mt-8 mb-4">Coach Invitations</div>
              <div class="text-left">
                <coach-invite-list
                  .importedCoaches=${coaches}
                  mode="import"
                ></coach-invite-list>
              </div>
            `
          : nothing}

        <button class="btn btn-primary mt-6" @click=${this.reset}>
          Import Another
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'league-import-wizard': LeagueImportWizard;
  }
}
